/* Lender Portal Wave 2/3 + Layer A pages (iter-90 · Feb 2026).
   5 new exports mounted under `/lender/*` alongside the existing 6 pages:
     • LenderWallet         — wallet balance + topup/withdraw + 30d KPIs
     • LenderActiveLoans    — full loan-book roster from new `loans` collection
     • LenderAging          — aging buckets for the new loans entity
     • LenderForecast       — 7/30/60/90-day expected inflows + runway
     • LenderPendingGrants  — Layer A approval workflow

   Theme: violet (matches existing LenderPages.jsx).
*/
import { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import {
  Banknote, ArrowDownToLine, ArrowUpFromLine, RefreshCw, FileText,
  AlertTriangle, TrendingUp, Clock, Wallet, ShieldCheck, Send, X,
  CheckCircle, Calendar, BarChart3, Activity,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../../components/ui/dialog";
import TradeScoreBadge from "../../components/TradeScoreBadge";

const fmtMoney = (n) => n == null ? "—" : new Intl.NumberFormat("en-US", {
  style: "currency", currency: "USD", maximumFractionDigits: 0,
}).format(n);
const fmtMoneyP = (n) => n == null ? "—" : new Intl.NumberFormat("en-US", {
  style: "currency", currency: "USD", maximumFractionDigits: 2,
}).format(n);
const fmtDate = (s) => { try { return new Date(s).toLocaleDateString(); } catch { return s || "—"; } };
const fmtDateTime = (s) => { try { return new Date(s).toLocaleString(); } catch { return s || "—"; } };

const STATUS_TONE = {
  active: "emerald", past_due: "amber",
  past_due_tier2: "orange", past_due_tier3: "rose",
  insurance_takeover: "violet", paid: "slate",
  nsf_pending: "rose", extended: "sky", void: "slate",
};

const PHASE_META = {
  active:              { label: "Active",                tone: "emerald" },
  nsf_retry:           { label: "NSF retry",             tone: "amber"   },
  grace:               { label: "Grace",                 tone: "orange"  },
  pre_takeover_grace:  { label: "Pre-Allianz grace",     tone: "rose"    },
  allianz_taken:       { label: "Allianz",               tone: "violet"  },
  paid:                { label: "Paid",                  tone: "slate"   },
  void:                { label: "Void",                  tone: "slate"   },
};

function PhasePill({ phase }) {
  if (!phase) return null;
  const meta = PHASE_META[phase.phase] || { label: phase.phase, tone: "slate" };
  const tone = meta.tone;
  return (
    <span data-testid={`phase-pill-${phase.phase}`}
      className={`px-2 py-0.5 rounded-full text-[10px] bg-${tone}-50 text-${tone}-700 border border-${tone}-200`}>
      {meta.label}
      {phase.phase !== "paid" && phase.phase !== "void" && phase.phase !== "allianz_taken" && phase.days_to_takeover > 0 && (
        <> · Allianz in {phase.days_to_takeover}d</>
      )}
    </span>
  );
}

function Kpi({ label, value, sub, icon: Icon, tone = "slate", testid }) {
  return (
    <div data-testid={testid} className={`rounded-xl border bg-${tone}-50 border-${tone}-200 px-4 py-3`}>
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
        {Icon && <Icon className={`w-4 h-4 text-${tone}-500`} />}
      </div>
      <div className="text-2xl font-semibold tabular-nums text-slate-900 mt-1.5">{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function StatusPill({ status }) {
  const tone = STATUS_TONE[status] || "slate";
  return (
    <span className={`px-2 py-0.5 rounded-full text-[10px] bg-${tone}-50 text-${tone}-700 border border-${tone}-200`}>
      {status?.replace(/_/g, " ")}
    </span>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// 1. LENDER WALLET (Wave 1 surfacing via UI)
// ═══════════════════════════════════════════════════════════════════════
export function LenderWallet() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [topupOpen, setTopupOpen] = useState(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("ach");
  const [reference, setReference] = useState("");
  // Wave B · iter-102 — TabaPay ACH pull fields
  const [bankRouting, setBankRouting] = useState("");
  const [bankAccount, setBankAccount] = useState("");
  const [bankAccountHolder, setBankAccountHolder] = useState("");
  const [bankAccountType, setBankAccountType] = useState("Checking");
  const [pendingTopups, setPendingTopups] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/partner/lender/wallet");
      setData(data);
      try {
        const { data: pt } = await api.get("/partner/lender/wallet/pending-topups");
        setPendingTopups(pt.rows || []);
      } catch { /* ignore */ }
    } catch (e) {
      toast.error("Couldn't load wallet");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const doTopup = async () => {
    const n = Number(amount);
    if (!Number.isFinite(n) || n <= 0) return toast.error("Enter a positive amount");
    setBusy(true);
    try {
      const payload = { amount: n, method, reference };
      // Live ACH pull if bank fields supplied (TabaPay path)
      if (bankRouting && bankAccount && method === "ach") {
        payload.bank_routing = bankRouting;
        payload.bank_account = bankAccount;
        payload.bank_account_type = bankAccountType;
        if (bankAccountHolder) payload.bank_account_holder = bankAccountHolder;
      }
      const { data: r } = await api.post("/partner/lender/wallet/topup", payload);
      if (r.status === "pending") {
        toast.success(`ACH pull initiated · settlement in 1-3 business days`);
      } else if (r.status === "failed") {
        toast.error(`Top-up failed: ${r.failure_reason || "unknown"}`);
      } else {
        toast.success(`Topped up ${fmtMoneyP(n)}`);
      }
      setTopupOpen(false); setAmount(""); setReference("");
      setBankRouting(""); setBankAccount(""); setBankAccountHolder("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Top-up failed");
    } finally { setBusy(false); }
  };

  const doWithdraw = async () => {
    const n = Number(amount);
    if (!Number.isFinite(n) || n <= 0) return toast.error("Enter a positive amount");
    setBusy(true);
    try {
      await api.post("/partner/lender/wallet/withdraw", { amount: n, method, reference });
      toast.success(`Withdrawal of ${fmtMoneyP(n)} initiated`);
      setWithdrawOpen(false); setAmount(""); setReference("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.reason || e?.response?.data?.detail?.message || "Withdraw failed");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="lender-wallet" className="space-y-5">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="display text-3xl tracking-tight flex items-center gap-2.5">
            <Wallet className="w-6 h-6 text-violet-500"/> Wallet
          </h1>
          <p className="text-sm text-slate-600 mt-1">Fund your wallet to extend credit to buyers. Disbursements pull from here, repayments land here.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setTopupOpen(true)} data-testid="lender-topup-btn">
            <ArrowDownToLine className="w-4 h-4 mr-1.5"/> Top up
          </Button>
          <Button variant="outline" onClick={() => setWithdrawOpen(true)} data-testid="lender-withdraw-btn">
            <ArrowUpFromLine className="w-4 h-4 mr-1.5"/> Withdraw
          </Button>
          <Button variant="outline" onClick={load} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`}/> Refresh
          </Button>
        </div>
      </header>

      {data && (
        <>
          <div className="rounded-2xl border bg-gradient-to-br from-violet-50 to-white p-7">
            <div className="text-[11px] uppercase tracking-wider text-violet-600">Available balance</div>
            <div className="display text-6xl tracking-tight mt-2 tabular-nums" data-testid="lender-balance">
              {fmtMoneyP(data.balance)}
            </div>
            <div className="text-xs text-slate-500 mt-2">Currency: {data.currency} · Wallet ID: <span className="font-mono">{data.wallet_user_id}</span></div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Kpi testid="lender-w-inflow" label="30d inflow" value={fmtMoneyP(data.kpis_30d?.inflow_usd)} sub={`${data.kpis_30d?.inflow_count || 0} txns`} tone="emerald" icon={ArrowDownToLine}/>
            <Kpi testid="lender-w-outflow" label="30d outflow" value={fmtMoneyP(data.kpis_30d?.outflow_usd)} sub={`${data.kpis_30d?.outflow_count || 0} txns`} tone="amber" icon={ArrowUpFromLine}/>
            <Kpi testid="lender-w-net" label="30d net" value={fmtMoneyP((data.kpis_30d?.inflow_usd || 0) - (data.kpis_30d?.outflow_usd || 0))} tone="violet"/>
            <Kpi testid="lender-w-currency" label="Currency" value={data.currency || "USD"} tone="slate"/>
          </div>

          {pendingTopups.length > 0 && (
            <div data-testid="lender-pending-topups" className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
              <div className="text-sm font-semibold text-amber-900 flex items-center gap-2 mb-2">
                <Clock className="w-4 h-4"/> Pending topups ({pendingTopups.length})
              </div>
              <div className="text-[11px] text-amber-700 mb-2">
                These ACH pulls are in flight. Balance updates when TabaPay confirms settlement (1-3 business days).
              </div>
              <div className="rounded-md border bg-white divide-y">
                {pendingTopups.map((t) => (
                  <div key={t.id} className="px-3 py-2 text-xs flex items-center gap-3">
                    <span className={`px-1.5 py-0.5 rounded-full border text-[10px] ${t.status === "failed" ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                      {t.status}
                    </span>
                    <span className="tabular-nums font-medium">{fmtMoneyP(t.amount)}</span>
                    <span className="text-slate-500 font-mono truncate flex-1">{t.ref}</span>
                    <span className="text-slate-400">{fmtDateTime(t.created_at)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="rounded-2xl border bg-white">
            <div className="px-4 py-3 border-b text-sm font-medium text-slate-700 flex items-center gap-2">
              <Activity className="w-4 h-4"/> Recent transactions ({data.transactions?.length || 0})
            </div>
            <div className="grid grid-cols-12 px-4 py-2 bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500 border-b">
              <div className="col-span-3">When</div>
              <div className="col-span-2">Type</div>
              <div className="col-span-2">Ref</div>
              <div className="col-span-3">Reconciliation</div>
              <div className="col-span-2 text-right">Amount</div>
            </div>
            <div data-testid="lender-w-txns">
              {data.transactions?.length === 0 && <div className="text-center text-sm text-slate-500 py-10">No transactions yet.</div>}
              {data.transactions?.map((t) => {
                const recon = t.metadata?.loan_number || t.metadata?.invoice_code || "—";
                return (
                  <div key={t.id} className="grid grid-cols-12 px-4 py-2 border-b text-sm hover:bg-slate-50/70">
                    <div className="col-span-3 text-xs text-slate-500">{fmtDateTime(t.created_at)}</div>
                    <div className="col-span-2 text-xs"><Badge variant="outline">{t.type}</Badge></div>
                    <div className="col-span-2 text-xs font-mono truncate">{t.ref}</div>
                    <div className="col-span-3 text-xs text-slate-600 font-mono truncate">{recon}</div>
                    <div className={`col-span-2 text-right tabular-nums text-sm ${t.amount < 0 ? "text-rose-600" : "text-emerald-600"}`}>
                      {fmtMoneyP(t.amount)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* Top-up dialog */}
      <Dialog open={topupOpen} onOpenChange={setTopupOpen}>
        <DialogContent data-testid="topup-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Top up lender wallet</DialogTitle>
            <DialogDescription>
              Pull funds from your external bank account. Supply bank details for a real ACH pull · leave blank for mock-mode instant-settle (demo / preview).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Amount (USD)</Label><Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="topup-amount" /></div>
            <div>
              <Label>Method</Label>
              <Select value={method} onValueChange={setMethod}>
                <SelectTrigger data-testid="topup-method"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ach">ACH (1-2 business days)</SelectItem>
                  <SelectItem value="wire">Wire (same day)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="rounded-lg border border-violet-200 bg-violet-50 px-3 py-2.5 text-xs text-violet-900">
              <strong>Bank details (optional — TabaPay ACH pull)</strong>
              <div className="mt-1 text-[11px] text-violet-700">
                Filling these in initiates a real ACH debit. Funds settle in 1-3 business days. Skip to use mock-mode.
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Routing #</Label><Input value={bankRouting} onChange={(e) => setBankRouting(e.target.value)} placeholder="9 digits" maxLength={9} data-testid="topup-routing"/></div>
              <div><Label>Account #</Label><Input value={bankAccount} onChange={(e) => setBankAccount(e.target.value)} placeholder="Checking / Savings" data-testid="topup-account"/></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Account type</Label>
                <Select value={bankAccountType} onValueChange={setBankAccountType}>
                  <SelectTrigger data-testid="topup-account-type"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Checking">Checking</SelectItem>
                    <SelectItem value="Savings">Savings</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Account holder</Label><Input value={bankAccountHolder} onChange={(e) => setBankAccountHolder(e.target.value)} placeholder="Legal name" data-testid="topup-holder"/></div>
            </div>
            <div><Label>Reference (optional)</Label><Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Internal memo" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTopupOpen(false)}>Cancel</Button>
            <Button onClick={doTopup} disabled={busy} data-testid="topup-submit">{busy ? "Processing…" : "Top up"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Withdraw dialog */}
      <Dialog open={withdrawOpen} onOpenChange={setWithdrawOpen}>
        <DialogContent data-testid="withdraw-dialog">
          <DialogHeader>
            <DialogTitle>Withdraw from lender wallet</DialogTitle>
            <DialogDescription>Send funds back to your external bank account.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Amount (USD)</Label><Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="withdraw-amount" /></div>
            <div>
              <Label>Method</Label>
              <Select value={method} onValueChange={setMethod}>
                <SelectTrigger data-testid="withdraw-method"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ach">ACH</SelectItem>
                  <SelectItem value="wire">Wire</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWithdrawOpen(false)}>Cancel</Button>
            <Button onClick={doWithdraw} disabled={busy} data-testid="withdraw-submit">{busy ? "Processing…" : "Withdraw"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// 2. ACTIVE LOANS (full book — new `loans` collection)
// ═══════════════════════════════════════════════════════════════════════
export function LenderActiveLoans() {
  const [data, setData] = useState({ rows: [], kpis: {}, by_status: {} });
  const [status, setStatus] = useState("all");
  const [terms, setTerms] = useState("all");
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (status !== "all") params.status = status;
      if (terms !== "all") params.terms = terms;
      if (search) params.q = search;
      const { data } = await api.get("/partner/lender/loans-roster", { params });
      setData(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, terms]);

  return (
    <div data-testid="lender-active-loans" className="space-y-5">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="display text-3xl tracking-tight flex items-center gap-2.5">
            <FileText className="w-6 h-6 text-violet-500"/> Active loans
          </h1>
          <p className="text-sm text-slate-600 mt-1">Your live book of business — every loan funded by your wallet, with reconciliation back to invoice + order.</p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`}/> Refresh
        </Button>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="lender-al-kpis">
        <Kpi testid="al-kpi-outstanding-principal" label="Outstanding principal" value={fmtMoney(data.kpis?.outstanding_principal_usd)} tone="violet" icon={Banknote} sub={`${data.kpis?.outstanding_count || 0} open loans`}/>
        <Kpi testid="al-kpi-outstanding-fees" label="Outstanding fees" value={fmtMoneyP(data.kpis?.outstanding_fees_usd)} tone="emerald" icon={TrendingUp}/>
        <Kpi testid="al-kpi-lifetime" label="Lifetime funded" value={fmtMoney(data.kpis?.total_funded_usd_lifetime)} tone="slate" icon={ShieldCheck}/>
        <Kpi testid="al-kpi-past-due-count" label="Past-due loans" value={(data.by_status?.past_due || 0) + (data.by_status?.past_due_tier2 || 0) + (data.by_status?.past_due_tier3 || 0)} tone={(data.by_status?.past_due || 0) ? "rose" : "slate"} icon={AlertTriangle}/>
      </div>

      <div className="flex flex-wrap gap-2 items-center rounded-2xl border bg-white px-4 py-3">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-44 h-9" data-testid="al-filter-status"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {["active", "past_due", "past_due_tier2", "past_due_tier3", "insurance_takeover", "paid", "nsf_pending", "extended", "void"].map((s) => (
              <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={terms} onValueChange={setTerms}>
          <SelectTrigger className="w-36 h-9" data-testid="al-filter-terms"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All terms</SelectItem>
            {["net14", "net30", "net60", "net90", "net120"].map((t) => <SelectItem key={t} value={t}>{t.toUpperCase()}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input placeholder="Search loan# / invoice# / order#" value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} className="flex-1 h-9 min-w-[200px]" data-testid="al-search"/>
        <Button variant="outline" onClick={load} className="h-9" data-testid="al-search-btn">Search</Button>
      </div>

      <div className="rounded-2xl border bg-white">
        <div className="grid grid-cols-12 px-4 py-2 bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500 border-b">
          <div className="col-span-2">Loan #</div>
          <div className="col-span-2">Buyer</div>
          <div className="col-span-1">Terms</div>
          <div className="col-span-2">Principal</div>
          <div className="col-span-2">Phase</div>
          <div className="col-span-2">Due</div>
          <div className="col-span-1 text-right">DPD</div>
        </div>
        <div data-testid="al-table">
          {data.rows.length === 0 && <div className="text-center text-sm text-slate-500 py-10" data-testid="al-empty">No loans match your filters.</div>}
          {data.rows.map((r) => (
            <button key={r.id} data-testid={`al-row-${r.id}`} onClick={() => setOpen(r)}
              className="w-full text-left grid grid-cols-12 px-4 py-2 border-b text-sm hover:bg-slate-50/70">
              <div className="col-span-2 font-mono text-xs text-slate-700 flex items-center gap-1.5">
                {r.loan_number}
                {r.lender_flag?.active && (
                  <span data-testid={`al-flag-badge-${r.id}`}
                    title={r.lender_flag.reason || "Flagged for review"}
                    className={`text-[9px] px-1 py-0.5 rounded border bg-${r.lender_flag.severity === "critical" ? "rose" : r.lender_flag.severity === "warning" ? "amber" : "sky"}-50 text-${r.lender_flag.severity === "critical" ? "rose" : r.lender_flag.severity === "warning" ? "amber" : "sky"}-700 border-${r.lender_flag.severity === "critical" ? "rose" : r.lender_flag.severity === "warning" ? "amber" : "sky"}-200`}>
                    ⚑ {r.lender_flag.severity?.[0]?.toUpperCase()}
                  </span>
                )}
              </div>
              <div className="col-span-2 truncate">
                <div className="text-xs font-medium">{r.buyer_company || r.buyer_email?.split("@")[0] || "—"}</div>
                <div className="text-[10px] text-slate-500 truncate">{r.buyer_email}</div>
              </div>
              <div className="col-span-1 text-xs"><Badge variant="outline">{r.terms?.toUpperCase()}</Badge></div>
              <div className="col-span-2 tabular-nums text-sm">{fmtMoneyP(r.principal_remaining)}</div>
              <div className="col-span-2 text-xs"><PhasePill phase={r.loan_phase}/></div>
              <div className="col-span-2 text-xs text-slate-600">{fmtDate(r.due_date)}</div>
              <div className="col-span-1 text-right text-xs">
                <span className={r.days_past_due > 0 ? "text-rose-600 font-medium" : "text-slate-500"}>{r.days_past_due}d</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      <LoanDetailDialog loan={open} onClose={() => setOpen(null)}/>
    </div>
  );
}

function LoanDetailDialog({ loan, onClose }) {
  const [data, setData] = useState(null);
  const [flagOpen, setFlagOpen] = useState(false);
  const [flagBusy, setFlagBusy] = useState(false);

  const load = async () => {
    if (!loan) { setData(null); return; }
    const r = await api.get(`/partner/lender/loans/${loan.id}`);
    setData(r.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [loan?.id]);
  if (!loan) return null;

  const clearFlag = async () => {
    const reason = window.prompt("Reason for clearing this flag (≥3 chars):");
    if (!reason || reason.trim().length < 3) return;
    setFlagBusy(true);
    try {
      await api.post(`/partner/lender/loans/${loan.id}/clear-flag`, { reason });
      toast.success("Flag cleared");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Could not clear flag");
    } finally { setFlagBusy(false); }
  };

  return (
    <Dialog open={!!loan} onOpenChange={onClose}>
      <DialogContent data-testid="loan-detail-dialog" className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="font-mono text-base flex items-center gap-2">
            {loan.loan_number}
            {data?.lender_flag?.active && (
              <span data-testid="loan-detail-flag-badge" className="text-[10px] font-sans px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                ⚑ Flagged · {data.lender_flag.severity}
              </span>
            )}
          </DialogTitle>
          <DialogDescription>{loan.buyer_company || loan.buyer_email} · Invoice {loan.invoice_code} · Order {loan.order_code || "—"}</DialogDescription>
        </DialogHeader>
        {data && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Kpi label="Principal" value={fmtMoneyP(data.principal)} tone="violet"/>
              <Kpi label="Remaining" value={fmtMoneyP(data.principal_remaining)} tone="amber"/>
              <Kpi label="Fee owed" value={fmtMoneyP(data.fee_owed)} tone="emerald"/>
              <Kpi label="Total owed" value={fmtMoneyP(data.total_owed)} tone="slate"/>
            </div>
            <div className="rounded-lg border bg-slate-50 px-4 py-3 text-sm space-y-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] uppercase text-slate-500">Phase</span>
                <PhasePill phase={data.loan_phase}/>
                <span className="text-[10px] uppercase text-slate-500 ml-3">Status</span>
                <StatusPill status={data.status}/>
              </div>
              <div><span className="text-[10px] uppercase text-slate-500">Terms</span> {data.terms?.toUpperCase()} · opened {fmtDate(data.opened_at)} · due {fmtDate(data.due_date)}</div>
              <div><span className="text-[10px] uppercase text-slate-500">Days past due</span> {data.days_past_due}</div>
              {data.insurance_claim_id && <div className="text-violet-700"><span className="text-[10px] uppercase">Allianz claim</span> <span className="font-mono">{data.insurance_claim_id}</span> · taken over {fmtDate(data.insurance_takeover_at)}</div>}
            </div>

            {data.lender_flag?.active && (
              <div data-testid="loan-detail-flag-card" className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm">
                <div className="font-medium text-amber-900 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4"/> Flagged for review · {data.lender_flag.severity}
                </div>
                <div className="text-xs text-amber-800 mt-1">{data.lender_flag.reason}</div>
                <div className="text-[10px] text-amber-700 mt-1">By {data.lender_flag.flagged_by_email || "—"} · {fmtDateTime(data.lender_flag.flagged_at)}</div>
              </div>
            )}

            <div>
              <div className="text-sm font-medium text-slate-700 mb-1.5">Repayment attempts ({data.repayment_attempts?.length || 0})</div>
              <div className="rounded-lg border divide-y max-h-44 overflow-auto" data-testid="loan-detail-attempts">
                {(!data.repayment_attempts || data.repayment_attempts.length === 0) && <div className="text-sm text-slate-500 px-3 py-3">No attempts yet.</div>}
                {data.repayment_attempts?.map((a, i) => (
                  <div key={i} className="px-3 py-2 text-xs grid grid-cols-12 gap-2">
                    <div className="col-span-4 text-slate-500">{fmtDateTime(a.attempted_at)}</div>
                    <div className="col-span-2 tabular-nums">{fmtMoneyP(a.amount_paid ?? a.amount_due)}</div>
                    <div className="col-span-3 text-slate-500">bal {fmtMoneyP(a.wallet_balance_at_attempt)}</div>
                    <div className="col-span-3 text-right">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] ${a.outcome === "paid" ? "bg-emerald-50 text-emerald-700" : a.outcome === "partial" ? "bg-amber-50 text-amber-700" : "bg-rose-50 text-rose-700"}`}>{a.outcome}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="text-sm font-medium text-slate-700 mb-1.5">Reminders sent ({data.reminders_log?.length || 0})</div>
              <div className="rounded-lg border divide-y max-h-32 overflow-auto" data-testid="loan-detail-reminders">
                {(!data.reminders_log || data.reminders_log.length === 0) && <div className="text-sm text-slate-500 px-3 py-3">No reminders yet.</div>}
                {data.reminders_log?.map((r, i) => (
                  <div key={i} className="px-3 py-2 text-xs grid grid-cols-12">
                    <div className="col-span-5 text-slate-500">{fmtDateTime(r.at)}</div>
                    <div className="col-span-3 font-mono">{r.kind}</div>
                    <div className="col-span-4 text-right text-slate-500">{(r.channels || []).join(" · ")}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        <DialogFooter className="flex items-center justify-between gap-2">
          <div className="flex gap-2">
            {data?.lender_flag?.active ? (
              <Button variant="outline" size="sm" disabled={flagBusy} onClick={clearFlag} data-testid="loan-detail-clear-flag">
                <CheckCircle className="w-4 h-4 mr-1.5"/> Clear flag
              </Button>
            ) : (
              <Button variant="outline" size="sm" onClick={() => setFlagOpen(true)} data-testid="loan-detail-flag-btn"
                disabled={data?.status && ["paid", "void", "insurance_takeover"].includes(data.status)}>
                <AlertTriangle className="w-4 h-4 mr-1.5"/> Flag for review
              </Button>
            )}
          </div>
          <Button onClick={onClose}>Close</Button>
        </DialogFooter>

        <FlagLoanDialog
          loan={loan}
          open={flagOpen}
          onClose={() => setFlagOpen(false)}
          onFlagged={() => { setFlagOpen(false); load(); }}
        />
      </DialogContent>
    </Dialog>
  );
}

function FlagLoanDialog({ loan, open, onClose, onFlagged }) {
  const [form, setForm] = useState({ severity: "warning", reason: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setForm({ severity: "warning", reason: "" }); }, [open]);

  const submit = async () => {
    if (form.reason.trim().length < 3) return;
    setBusy(true);
    try {
      await api.post(`/partner/lender/loans/${loan.id}/flag-for-review`, {
        severity: form.severity,
        reason: form.reason.trim(),
      });
      toast.success("Loan flagged for review");
      onFlagged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Could not flag");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="flag-loan-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600"/> Flag {loan?.loan_number} for review
          </DialogTitle>
          <DialogDescription>
            Marks the loan with an internal review badge. <strong>No money moves</strong> — the loan keeps its terms and continues its auto-debit schedule.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Severity</Label>
            <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
              <SelectTrigger data-testid="flag-severity"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="info">Info — FYI signal</SelectItem>
                <SelectItem value="warning">Warning — concerning pattern</SelectItem>
                <SelectItem value="critical">Critical — escalate now</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Reason (≥3 chars · audit-logged)</Label>
            <Textarea
              data-testid="flag-reason"
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
              rows={3}
              placeholder="e.g. Sudden 40% drop in monthly sales volume; buyer not responding to outreach."
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy || form.reason.trim().length < 3} data-testid="flag-submit">
            {busy ? "Flagging…" : "Flag loan"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// 3. AGING (loan-aware buckets)
// ═══════════════════════════════════════════════════════════════════════
export function LenderAging() {
  const [data, setData] = useState({ buckets: {}, bucket_total_usd: {}, rows: {} });
  const [active, setActive] = useState("1_30");
  const load = () => api.get("/partner/lender/loans-aging").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);
  return (
    <div data-testid="lender-aging" className="space-y-5">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="display text-3xl tracking-tight flex items-center gap-2.5">
            <Clock className="w-6 h-6 text-violet-500"/> Aging
          </h1>
          <p className="text-sm text-slate-600 mt-1">How your open loans break down by days-past-due. Tier-ups apply at day 31 and day 60 (Allianz takeover).</p>
        </div>
        <Button variant="outline" onClick={load}><RefreshCw className="w-4 h-4 mr-1.5"/> Refresh</Button>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="aging-buckets">
        {[
          { k: "current", l: "Current", tone: "emerald", testid: "bucket-current" },
          { k: "1_30", l: "1–30 days", tone: "amber", testid: "bucket-1-30" },
          { k: "31_60", l: "31–60 days", tone: "orange", testid: "bucket-31-60" },
          { k: "60_plus", l: "60+ days", tone: "rose", testid: "bucket-60" },
          { k: "takenover", l: "Allianz taken", tone: "violet", testid: "bucket-takenover" },
        ].map((b) => (
          <button key={b.k} onClick={() => setActive(b.k)} className={`text-left ${active === b.k ? "ring-2 ring-violet-500 rounded-xl" : ""}`}>
            <Kpi testid={b.testid} label={b.l} value={data.buckets?.[b.k] || 0} sub={fmtMoneyP(data.bucket_total_usd?.[b.k])} tone={b.tone}/>
          </button>
        ))}
      </div>

      <div className="rounded-2xl border bg-white">
        <div className="px-4 py-3 border-b text-sm font-medium text-slate-700">
          {(active === "current" && "Current loans") ||
           (active === "1_30" && "1–30 days past due") ||
           (active === "31_60" && "31–60 days past due (Net-60 tier)") ||
           (active === "60_plus" && "60+ days past due") ||
           (active === "takenover" && "Allianz takeover (you've been made whole)")}
        </div>
        <div className="grid grid-cols-12 px-4 py-2 bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500 border-b">
          <div className="col-span-2">Loan #</div>
          <div className="col-span-3">Buyer</div>
          <div className="col-span-2">Invoice</div>
          <div className="col-span-2">Remaining</div>
          <div className="col-span-2">Due</div>
          <div className="col-span-1 text-right">DPD</div>
        </div>
        <div data-testid="aging-table">
          {(!data.rows?.[active] || data.rows[active].length === 0) && <div className="text-center text-sm text-slate-500 py-10" data-testid="aging-empty">No loans in this bucket.</div>}
          {data.rows?.[active]?.map((r) => (
            <div key={r.id} className="grid grid-cols-12 px-4 py-2 border-b text-sm">
              <div className="col-span-2 font-mono text-xs">{r.loan_number}</div>
              <div className="col-span-3 truncate">
                <div className="text-xs font-medium">{r.buyer_company || "—"}</div>
                <div className="text-[10px] text-slate-500 truncate">{r.buyer_email}</div>
              </div>
              <div className="col-span-2 font-mono text-xs">{r.invoice_code}</div>
              <div className="col-span-2 tabular-nums text-sm">{fmtMoneyP(r.principal_remaining)}</div>
              <div className="col-span-2 text-xs text-slate-600">{fmtDate(r.due_date)}</div>
              <div className="col-span-1 text-right text-xs"><span className={r.days_past_due > 0 ? "text-rose-600 font-medium" : "text-slate-500"}>{r.days_past_due || 0}d</span></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// 4. FORECAST
// ═══════════════════════════════════════════════════════════════════════
export function LenderForecast() {
  const [data, setData] = useState(null);
  const load = () => api.get("/partner/lender/loans-forecast").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);
  return (
    <div data-testid="lender-forecast" className="space-y-5">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="display text-3xl tracking-tight flex items-center gap-2.5">
            <BarChart3 className="w-6 h-6 text-violet-500"/> Forecast
          </h1>
          <p className="text-sm text-slate-600 mt-1">Predicted cash inflows from open loans. Use this to decide how much to keep funded in your wallet.</p>
        </div>
        <Button variant="outline" onClick={load}><RefreshCw className="w-4 h-4 mr-1.5"/> Refresh</Button>
      </header>

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="forecast-kpis">
            <Kpi testid="fc-7d"   label="Next 7 days"   value={fmtMoney(data.expected_inflows_usd?.["7d"])}   tone="emerald" icon={Calendar}/>
            <Kpi testid="fc-14d"  label="Next 14 days"  value={fmtMoney(data.expected_inflows_usd?.["14d"])}  tone="emerald" icon={Calendar}/>
            <Kpi testid="fc-30d"  label="Next 30 days"  value={fmtMoney(data.expected_inflows_usd?.["30d"])}  tone="emerald" icon={Calendar}/>
            <Kpi testid="fc-60d"  label="Next 60 days"  value={fmtMoney(data.expected_inflows_usd?.["60d"])}  tone="violet"  icon={Calendar}/>
            <Kpi testid="fc-90d"  label="Next 90 days"  value={fmtMoney(data.expected_inflows_usd?.["90d"])}  tone="violet"  icon={Calendar}/>
            <Kpi testid="fc-180d" label="Next 180 days" value={fmtMoney(data.expected_inflows_usd?.["180d"])} tone="sky"     icon={Calendar}/>
            <Kpi testid="fc-365d" label="Next 12 months" value={fmtMoney(data.expected_inflows_usd?.["365d"])} tone="sky"    icon={Calendar}/>
            <Kpi testid="fc-past-due" label="Past due (collecting)" value={fmtMoney(data.expected_inflows_usd?.past_due_collected_today)} tone="rose" icon={AlertTriangle}/>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="rounded-2xl border bg-white p-5">
              <div className="text-[11px] uppercase tracking-wider text-slate-500">Wallet balance</div>
              <div className="display text-3xl tracking-tight mt-1 tabular-nums" data-testid="fc-wallet-balance">{fmtMoneyP(data.wallet_balance_usd)}</div>
              <div className="text-xs text-slate-500 mt-2">Available for new disbursements</div>
            </div>
            <div className="rounded-2xl border bg-white p-5">
              <div className="text-[11px] uppercase tracking-wider text-slate-500">Avg daily disbursement (30d)</div>
              <div className="display text-3xl tracking-tight mt-1 tabular-nums" data-testid="fc-burn">{fmtMoneyP(data.avg_daily_burn_30d)}</div>
              <div className="text-xs text-slate-500 mt-2">New loans funded per day on average</div>
            </div>
            <div className="rounded-2xl border bg-white p-5">
              <div className="text-[11px] uppercase tracking-wider text-slate-500">Runway</div>
              <div className="display text-3xl tracking-tight mt-1 tabular-nums" data-testid="fc-runway">{data.runway_days != null ? `${data.runway_days} days` : "—"}</div>
              <div className={`text-xs mt-2 ${data.runway_days != null && data.runway_days < 14 ? "text-rose-600 font-medium" : "text-slate-500"}`}>
                {data.runway_days != null && data.runway_days < 14 ? "⚠ Top up the wallet soon" : "How long current balance lasts at recent burn"}
              </div>
            </div>
          </div>

          <div className="rounded-2xl border bg-white">
            <div className="px-4 py-3 border-b text-sm font-medium text-slate-700">Upcoming inflows (per day · next 120 days)</div>
            <div data-testid="fc-timeline" className="p-5">
              {(!data.timeline || data.timeline.length === 0) && <div className="text-center text-sm text-slate-500 py-6">No upcoming inflows.</div>}
              {data.timeline && data.timeline.length > 0 && (() => {
                const max = Math.max(...data.timeline.map((d) => d.amount));
                return (
                  <div className="flex items-end gap-px h-32">
                    {data.timeline.slice(0, 60).map((d) => (
                      <div key={d.date} title={`${d.date}: ${fmtMoneyP(d.amount)}`} className="flex-1 bg-violet-500 hover:bg-violet-600 transition-colors" style={{ height: `${(d.amount / max) * 100}%`, minHeight: "2px" }}/>
                    ))}
                  </div>
                );
              })()}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// 5. PENDING GRANTS (Layer A approval workflow)
// ═══════════════════════════════════════════════════════════════════════
export function LenderPendingGrants() {
  const [data, setData] = useState({ rows: [] });
  const [open, setOpen] = useState(null);
  const [decision, setDecision] = useState("approve");
  const [counterLimit, setCounterLimit] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/partner/lender/grants/pending").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (reason.trim().length < 3) return toast.error("Reason ≥ 3 chars required");
    setBusy(true);
    try {
      const body = { decision, reason };
      if (decision === "counter") body.counter_limit_usd = Number(counterLimit);
      await api.post(`/partner/lender/grants/${open.id}/decide`, body);
      toast.success(`Proposal ${decision}d`);
      setOpen(null); setReason(""); setCounterLimit("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.reason || "Decision failed");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="lender-pending-grants" className="space-y-5">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="display text-3xl tracking-tight flex items-center gap-2.5">
            <Send className="w-6 h-6 text-violet-500"/> Pending credit-line requests
          </h1>
          <p className="text-sm text-slate-600 mt-1">Buyers who passed Allianz underwriting and are waiting for your approval to activate their credit line.</p>
        </div>
        <Button variant="outline" onClick={load}><RefreshCw className="w-4 h-4 mr-1.5"/> Refresh</Button>
      </header>

      <Kpi testid="pg-total" label="Pending requests" value={data.rows.length} tone={data.rows.length ? "amber" : "slate"} icon={Send}/>

      <div className="rounded-2xl border bg-white">
        <div className="grid grid-cols-12 px-4 py-2 bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500 border-b">
          <div className="col-span-3">Buyer</div>
          <div className="col-span-2">Requested</div>
          <div className="col-span-2">Allianz grade</div>
          <div className="col-span-2">Default terms</div>
          <div className="col-span-2">Submitted</div>
          <div className="col-span-1 text-right">Action</div>
        </div>
        <div data-testid="pg-table">
          {data.rows.length === 0 && <div className="text-center text-sm text-slate-500 py-10" data-testid="pg-empty">No pending requests.</div>}
          {data.rows.map((r) => (
            <div key={r.id} className="grid grid-cols-12 px-4 py-2 border-b text-sm items-center" data-testid={`pg-row-${r.id}`}>
              <div className="col-span-3 truncate">
                <div className="text-xs font-medium">{r.buyer_company || "—"}</div>
                <div className="text-[10px] text-slate-500 truncate">{r.buyer_email}</div>
              </div>
              <div className="col-span-2 tabular-nums font-medium">{fmtMoney(r.requested_limit_usd)}</div>
              <div className="col-span-2 text-xs">{r.allianz_grade ? <Badge variant="outline">{r.allianz_grade} · {r.allianz_outlook || "n/a"}</Badge> : <span className="text-slate-400">—</span>}</div>
              <div className="col-span-2 text-xs">{r.default_terms?.toUpperCase()}</div>
              <div className="col-span-2 text-xs text-slate-500">{fmtDate(r.created_at)}</div>
              <div className="col-span-1 text-right">
                <Button size="sm" variant="outline" onClick={() => setOpen(r)} data-testid={`pg-decide-${r.id}`}>Decide</Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Decision dialog */}
      <Dialog open={!!open} onOpenChange={() => setOpen(null)}>
        <DialogContent data-testid="pg-decide-dialog">
          <DialogHeader>
            <DialogTitle>Decide credit-line request</DialogTitle>
            <DialogDescription>{open?.buyer_company} · {open?.buyer_email}</DialogDescription>
          </DialogHeader>
          {open && (
            <div className="space-y-3">
              <div className="rounded-lg border bg-slate-50 px-4 py-3 text-sm space-y-1">
                <div><span className="text-[10px] uppercase text-slate-500">Requested</span> <span className="tabular-nums">{fmtMoney(open.requested_limit_usd)}</span></div>
                <div><span className="text-[10px] uppercase text-slate-500">Terms</span> {open.default_terms?.toUpperCase()}</div>
                <div><span className="text-[10px] uppercase text-slate-500">Allianz</span> grade {open.allianz_grade || "—"} · outlook {open.allianz_outlook || "—"}</div>
                {open.buyer_id && <div className="flex items-center gap-2"><span className="text-[10px] uppercase text-slate-500">Trade Score</span> <TradeScoreBadge userId={open.buyer_id} size="sm"/></div>}
              </div>
              <div>
                <Label>Decision</Label>
                <Select value={decision} onValueChange={setDecision}>
                  <SelectTrigger data-testid="pg-decision"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="approve">Approve at requested limit</SelectItem>
                    <SelectItem value="counter">Counter at lower limit</SelectItem>
                    <SelectItem value="reject">Reject</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {decision === "counter" && (
                <div><Label>Counter limit (USD)</Label><Input type="number" value={counterLimit} onChange={(e) => setCounterLimit(e.target.value)} data-testid="pg-counter-limit"/></div>
              )}
              <div><Label>Reason (audit log)</Label><Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} data-testid="pg-reason" placeholder="Why approve / counter / reject?"/></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(null)}>Cancel</Button>
            <Button onClick={submit} disabled={busy || reason.trim().length < 3} data-testid="pg-submit">{busy ? "Submitting…" : "Submit decision"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
